import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { BUYER, SERVICE_PROVIDER, SUPPLIER, TableKey, TableView, User, type AccountType } from '@tradecafe/types/core';
import { DeepReadonly } from '@tradecafe/types/utils';
import { cloneDeep, compact, map, merge, pick, remove, uniqBy } from 'lodash-es';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, switchMap, map as rxMap } from 'rxjs/operators';
import { TableViewApiService } from 'src/api/table-view';
import {
  loadAccounts, selectAccountEntities,
  selectBuyersOptions,
  selectProvidersOptions,
  selectSuppliersOptions
} from 'src/app/store/accounts';
import { loadUsers, selectUserEntities } from 'src/app/store/users';
import { TableIdentities } from 'src/constants/table-identities';
import { environment } from 'src/environments/environment';
import { ToasterService } from 'src/shared/toaster/toaster.service';
import { selectOptions } from 'src/shared/utils/select-options';
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty';
import {
  availableServiceProviderShippingLogHeaders,
  availableShippingLogHeaders,
  availableSupplierShippingLogHeaders, columnsMap, ShippingLogHeader
} from './client-shipping-log-headers';
import { PreviewClientShippingLogService } from './preview/preview-client-shipping-log.service';

type NumberBoolean = 0 | 1;

const mapColumns = (columns, cMap) => columns.filter((i) => cMap[i]).map((i) => cMap[i]);

const tableIdentityMap = {
  [BUYER]: TableKey.ClientShippingLogList,
  [SUPPLIER]: TableKey.ClientSupplierShippingLogList,
  [SERVICE_PROVIDER]: TableKey.ClientServiceProviderShippingLogList,
};

@Component({
  selector: 'tc-logistics-client-shipping-log',
  templateUrl: './client-shipping-log.component.html',
  styleUrls: ['./client-shipping-log.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LogisticsClientShippingLogComponent implements OnInit {
  private store = inject(Store)
  private fb = inject(UntypedFormBuilder)
  private TableViewApi = inject(TableViewApiService)
  private previewClientShippingLogService = inject(PreviewClientShippingLogService)
  private toaster = inject(ToasterService)
  private sanitizer = inject(DomSanitizer)
  private chRef = inject(ChangeDetectorRef)
  private destroyRef = inject(DestroyRef)


  styles = this.sanitizer.bypassSecurityTrustHtml(`<style>body::-webkit-scrollbar { display: none; }<style>`);

  displayColumns = [];

  protected readonly filtersForm = this.fb.group({
    buyer: [],
    supplier: [],
    carrier: [],
  });

  form = new FormGroup({
    language: new FormControl<null | string>(null),
    considered_days: new FormControl(0),
    not_report: new FormControl<NumberBoolean>(0),
    email: new FormGroup({
      additional: new FormControl<string>(''),
      coordinator: new FormControl<NumberBoolean>(0),
      selling_trader: new FormControl<NumberBoolean>(0),
    }),
  });

  protected selectedAccountType: AccountType;
  protected account: number;
  protected accountType: AccountType;
  protected accountHeaders: ShippingLogHeader[] = [];
  protected selectedHeaders: ShippingLogHeader[] = [];
  protected logisticsCoordinator: DeepReadonly<User>;
  protected tableViewId: string;
  protected table: TableKey;

  protected buyers$ = selectOptions(this.store, selectBuyersOptions, this.filtersForm.controls.buyer);
  protected suppliers$ = selectOptions(this.store, selectSuppliersOptions, this.filtersForm.controls.buyer);
  protected serviceProvider$ = selectOptions(this.store, selectProvidersOptions, this.filtersForm.controls.buyer);
  private accounts$ = this.store.pipe(select(selectAccountEntities), waitNotEmpty());
  private users$ = this.store.pipe(select(selectUserEntities), waitNotEmpty());
  protected contacts: DeepReadonly<User[]>;

  openPreview() {
    this.previewClientShippingLogService.show({
      displayColumns: mapColumns(this.selectedHeaders.map((i) => i.id), columnsMap[this.selectedAccountType]),
      selectedAccountType: this.selectedAccountType,
      account: this.account,
      tableIdentity: tableIdentityMap[this.selectedAccountType],
    })
  }

  fetchColumns (accountId) {
    return this.TableViewApi.list(accountId, { user_id: accountId, table: this.table }).then(({ data }) => {
      let headers:  ShippingLogHeader[] = cloneDeep(this.getHeaders()).filter(
        col => col.id !== 'carrier_eta' || environment.enableMacropointCarrierEta
      ); // feature flagged columns

      // If the account has a stored table view
      if (data && data.length > 0) {
        // Pull the info out of the TableView
        const { columns, table_view_id, attributes } = data[0];
        this.tableViewId = table_view_id;

        // Map the headers being checked on load to the ones that are in the columns
        // of the view we pulled (_OR_ are specifically required by definition)
        headers = headers.map(header => ({
          ...header,
          disabled: header.disabled || header.required,
          selected: columns.indexOf(header.id) > -1 || header.required,
        }));

        // If the account type is a buyer, then we have some form fields to populate
        // from the table view attributes
        if (this.selectedAccountType === BUYER) {
          if (attributes) {
            const fields = pick(attributes, ['language', 'considered_days', 'not_report', 'email']);
            const values = merge(this.form.getRawValue(), fields);

            this.form.setValue(values);
          }
        }

        const requiredHeaders = headers.filter(header => header.required === true);
        // Any missing required columns need to be included, but they'll be put on the end
        this.selectedHeaders = compact(uniqBy(columns.map(
          (column) => headers.find((i) => i.id === column)
        ).concat(requiredHeaders), 'id'));
      } else {
        // Otherwise, use the headers list to populate the default list of table view headers that are selected
        this.selectedHeaders = headers.filter((header) => header.selected || header.required);
      }

      // Ok, we're done, pull the curtain on the page
      this.accountHeaders = headers;
      this.account = accountId;
      this.accountType = this.selectedAccountType;
      this.chRef.detectChanges();
    }).catch(() => this.toaster.error('Unable to load shipping log columns'));
  }

  getHeaders () {
    switch (this.selectedAccountType) {
      case BUYER:
        return availableShippingLogHeaders;
      case SUPPLIER:
        return availableSupplierShippingLogHeaders;
      case SERVICE_PROVIDER:
        return availableServiceProviderShippingLogHeaders;
      default:
        return availableShippingLogHeaders;
    }
  }

  changeSelectedHeader (header)  {
    // disabled and required headers cannot be changed
    if (header.disabled || header.required) {
      return;
    }

    header.selected = !header.selected;
    if (header.selected) {
      this.selectedHeaders.push(header);
    } else {
      remove(this.selectedHeaders, { id: header.id });
    }
  }

  dropHeader(e) {
    moveItemInArray(this.selectedHeaders, e.previousIndex, e.currentIndex);
  }

  resetPage() {
    this.accountHeaders = [];
    this.selectedHeaders = [];
    this.selectedAccountType = null;
    this.account = null;
    this.tableViewId = null;
    this.logisticsCoordinator = null;
    this.contacts = [];
    this.form.reset();
  }


  ngOnInit(): void {
    // Load all the accounts and all the users so we have access to that data
    this.store.dispatch(loadAccounts({}));
    this.store.dispatch(loadUsers({}));

    // There are 3 different dropdowns that we can use, one for each account type
    this.filtersForm.controls.supplier.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((supplierValue) => {
      this.resetPage();
      if (!supplierValue) {
        return;
      }
      this.selectedAccountType = SUPPLIER;
      this.filtersForm.controls.buyer.patchValue(null, { emitEvent: false, onlySelf: true });
      this.filtersForm.controls.carrier.patchValue(null, { emitEvent: false, onlySelf: true });
      this.table = TableIdentities.admin.supplierShippingLogList;
      this.fetchColumns(supplierValue);
    })

    this.filtersForm.controls.buyer.valueChanges.pipe(
      distinctUntilChanged(),
      switchMap((buyerValue) => combineLatest([this.accounts$, this.users$]).pipe(rxMap(([accounts, users]) => ({ buyerValue, accounts, users })))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ buyerValue, accounts, users }) => {
      this.resetPage();
      if (!buyerValue) {
        return;
      }
      this.selectedAccountType = BUYER;
      this.filtersForm.controls.supplier.patchValue(null, { emitEvent: false, onlySelf: true })
      this.filtersForm.controls.carrier.patchValue(null, { emitEvent: false, onlySelf: true })
      this.table = TableIdentities.admin.clientShippingLogList
      this.fetchColumns(buyerValue);

      // Buyers have some extra actions where we need to get the logistics coordinator info too
      this.logisticsCoordinator = users[accounts[buyerValue].coordinator];
      const userIdNumber = buyerValue * 1;
      this.contacts = Object.values(users).filter((i) => i.account === userIdNumber);
    })

    this.filtersForm.controls.carrier.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((spValue) => {
      this.resetPage();
      if (!spValue) {
        return;
      }
      this.selectedAccountType = SERVICE_PROVIDER;
      this.filtersForm.controls.buyer.patchValue(null, { emitEvent: false, onlySelf: true });
      this.filtersForm.controls.supplier.patchValue(null, { emitEvent: false, onlySelf: true });
      this.table = TableIdentities.admin.serviceProviderShippingLogList;
      this.fetchColumns(spValue);
    })
    }

  publish() {
    const account = this.account;
    const columns = map(this.selectedHeaders, 'id');

    const publishResult = (result) => {
      result.then((data) => {
        if (data.data) {
          this.tableViewId = data.data.table_view_id;
        }
        this.toaster.success('Published successfully');
      }, (e) => {
        this.toaster.error('Unable to publish');
      })
    }
    const v = this.form.value;

    const payload: Partial<TableView> = {
      attributes: {
        not_report: v.not_report ? 1 : 0,
        language: v.language,
        considered_days: v.considered_days,
        email: {
          additional: v.email?.additional,
          coordinator: v.email?.coordinator ? 1 : 0,
          selling_trader: v.email?.selling_trader ? 1 : 0,
        },
        version: 4,
      },
      columns,
      table: this.table,
      account: undefined,
      user_id: undefined,
    };

    if (this.selectedAccountType !== BUYER) {
      delete payload.attributes;
    }

    if (this.tableViewId) {
      publishResult(this.TableViewApi.update(account, this.tableViewId, payload));
      return;
    }

    payload.account = account + ''; // to stringconst
    payload.user_id = payload.account; // account specific, use account instead of user_id

    publishResult(this.TableViewApi.create(account, payload));
  }

 isBuyerAccount ()  {
    return this.selectedAccountType === BUYER;
  }
}
